import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import {
  painelModalCancelButtonClass,
  painelPermissionsModalScrollClass,
} from '@/components/painel/painel-styles';
import { GsImage } from '@/components/help-center/latest-power-shared';
import {
  PawnNavIcon,
  PowerNavIcon,
  SmileNavIcon,
} from '@/components/help-center/MaskedSvgIcon';
import { usePowersCms } from '@/components/painel/powers/powers-cms-context';
import { withoutAcePowers } from '@/lib/powers-data';
import { normalizePowerSlug } from '@/lib/power-mention-index';
import type { LucideIcon } from 'lucide-react';

export type PowerBbcodeMode = 'mention' | 'smilies' | 'pawns';

export type InsertPowerBbcodeDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onInsert: (bbcode: string) => void;
  mode: PowerBbcodeMode;
  dark?: boolean;
};

type PowerOption = { slug: string; name: string };

const MODE_META: Record<
  PowerBbcodeMode,
  {
    title: string;
    help: string;
    example: string;
    Icon: LucideIcon;
    format: (slug: string, name?: string) => string;
  }
> = {
  mention: {
    title: 'Mencionar power',
    help: 'Insere uma menção interativa ao power. O texto visível usa o nome tal como no catálogo.',
    example: '[[NomeDoPower]]',
    Icon: PowerNavIcon,
    format: (slug, name) => `[[${name?.trim() || slug}]]`,
  },
  smilies: {
    title: 'Galeria de smilies',
    help: 'Insere a grelha de smilies do power (com toggle Claro/Escuro).',
    example: '[[smilies:bowgal]]',
    Icon: SmileNavIcon,
    format: (slug) => `[[smilies:${slug}]]`,
  },
  pawns: {
    title: 'Galeria de peões',
    help: 'Insere a grelha de peões do power (com toggle Claro/Escuro).',
    example: '[[pawns:bowgal]]',
    Icon: PawnNavIcon,
    format: (slug) => `[[pawns:${slug}]]`,
  },
};

function buildPowerOptions(powers: { slug: string; name: string }[]): PowerOption[] {
  const seen = new Set<string>();
  const list: PowerOption[] = [];
  for (const p of withoutAcePowers(powers as never)) {
    const slug = String(p.slug ?? '').trim().toLowerCase();
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    list.push({ slug, name: p.name });
  }
  return list.sort((a, b) => a.name.localeCompare(b.name, 'pt'));
}

const MAX_RESULTS = 60;

export function InsertPowerBbcodeDialog({
  open,
  onOpenChange,
  onInsert,
  mode,
  dark: _dark = true,
}: InsertPowerBbcodeDialogProps) {
  // Modais de BBCode especiais usam sempre o tema escuro do painel.
  const dark = true;
  void _dark;
  const meta = MODE_META[mode];
  const Icon = meta.Icon;
  const { powers } = usePowersCms();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const options = useMemo(() => buildPowerOptions(powers), [powers]);
  const trimmedQuery = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (!trimmedQuery) return options.slice(0, MAX_RESULTS);
    return options
      .filter(
        (o) => o.name.toLowerCase().includes(trimmedQuery) || o.slug.includes(trimmedQuery)
      )
      .slice(0, MAX_RESULTS);
  }, [options, trimmedQuery]);

  const manualSlug = normalizePowerSlug(query);
  const hasExact = results.some((r) => r.slug === manualSlug);

  const insertSlug = (slug: string, name?: string) => {
    onInsert(meta.format(slug, name));
    onOpenChange(false);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (results[0]) insertSlug(results[0].slug, results[0].name);
    else if (manualSlug) insertSlug(manualSlug);
  };

  const body = (
    <>
      <div className={cn('space-y-4', dark ? 'px-6 py-5 sm:px-7' : 'px-6 py-5')}>
        <div className="relative">
          <Search
            className={cn(
              'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2',
              dark ? 'text-slate-500' : 'text-gray-400'
            )}
            aria-hidden
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nome ou slug…"
            autoFocus
            className={cn(
              dark
                ? cn(painelInputClass, 'pl-9')
                : 'h-10 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 font-inter text-sm text-gray-900 shadow-inner focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200'
            )}
            onKeyDown={handleSearchKeyDown}
          />
        </div>

        <div
          className={cn(
            'max-h-[min(42vh,360px)] overflow-y-auto rounded-xl',
            dark
              ? cn('border border-white/10 bg-blue-950/30', painelPermissionsModalScrollClass)
              : 'border border-gray-200 bg-gray-50/50'
          )}
        >
          {results.length === 0 ? (
            <p
              className={cn(
                'px-4 py-10 text-center font-inter text-sm',
                dark ? 'text-slate-500' : 'text-gray-500'
              )}
            >
              Nenhum power encontrado.
            </p>
          ) : (
            <ul className={cn('divide-y p-1.5', dark ? 'divide-white/[0.06]' : 'divide-gray-100')}>
              {results.map((o) => (
                <li key={o.slug}>
                  <button
                    type="button"
                    onClick={() => insertSlug(o.slug, o.name)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-left transition-all',
                      dark
                        ? 'hover:bg-sky-400/10 hover:ring-1 hover:ring-sky-400/20'
                        : 'hover:bg-blue-50'
                    )}
                  >
                    <GsImage
                      code={o.slug}
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border',
                        dark
                          ? 'border-white/10 bg-[#030023]/60 ring-1 ring-sky-400/10'
                          : 'border-gray-200 bg-white'
                      )}
                      imgClassName="h-8 w-8 object-contain"
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          'block truncate font-poppins text-sm font-semibold',
                          dark ? 'text-white' : 'text-gray-900'
                        )}
                      >
                        {o.name}
                      </span>
                      <span
                        className={cn(
                          'mt-0.5 block truncate font-mono text-[11px]',
                          dark ? 'text-slate-500' : 'text-gray-500'
                        )}
                      >
                        {meta.format(o.slug)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {manualSlug && !hasExact ? (
          <button
            type="button"
            onClick={() => insertSlug(manualSlug)}
            className={cn(
              'flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left',
              dark
                ? 'border-sky-400/25 bg-sky-500/10 hover:border-sky-400/40'
                : 'border-blue-200 bg-blue-50/50 hover:bg-blue-50'
            )}
          >
            <span className={cn('font-inter text-sm', dark ? 'text-slate-300' : 'text-gray-800')}>
              Inserir slug personalizado
            </span>
            <code
              className={cn(
                'shrink-0 rounded-lg px-2 py-1 font-mono text-xs',
                dark ? 'bg-white/10 text-sky-200' : 'text-blue-700'
              )}
            >
              {meta.format(manualSlug)}
            </code>
          </button>
        ) : null}
      </div>

      <div
        className={cn(
          'flex justify-end border-t px-6 py-4',
          dark ? 'border-white/10 bg-blue-950/25' : 'border-gray-100 bg-gray-50/50'
        )}
      >
        <Button
          type="button"
          variant="ghost"
          className={dark ? painelModalCancelButtonClass : 'rounded-xl'}
          onClick={() => onOpenChange(false)}
        >
          Cancelar
        </Button>
      </div>
    </>
  );

  if (dark) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="max-h-[90vh] max-w-lg overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
        >
          <DialogTitle className="sr-only">{meta.title}</DialogTitle>
          <PainelPermissionsDialogShell>
            <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6 sm:px-7">
              <div className="flex items-start gap-4 pr-12">
                <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500/25 to-sky-700/10 ring-1 ring-sky-400/35">
                  <Icon className="h-7 w-7 text-sky-200" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                    BBCode especial
                  </p>
                  <h2 className="mt-1 font-poppins text-xl font-bold text-white sm:text-2xl">
                    {meta.title}
                  </h2>
                  <p className="mt-1.5 font-inter text-sm text-slate-400">
                    {meta.help}{' '}
                    <code className="rounded-md bg-white/10 px-1.5 py-0.5 font-mono text-xs text-sky-200/90">
                      {meta.example}
                    </code>
                  </p>
                </div>
              </div>
            </div>
            {body}
          </PainelPermissionsDialogShell>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg gap-0 overflow-hidden border-gray-200 bg-white p-0 sm:rounded-2xl">
        <DialogTitle className="sr-only">{meta.title}</DialogTitle>
        <div className="border-b border-gray-100 bg-gray-50/80 px-6 pb-5 pt-6">
          <div className="flex items-start gap-4">
            <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-100 to-blue-50 ring-1 ring-blue-200">
              <Icon className="h-6 w-6 text-blue-600" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-blue-600/70">
                BBCode especial
              </p>
              <h2 className="mt-1 font-poppins text-xl font-bold text-gray-900">{meta.title}</h2>
              <p className="mt-1.5 font-inter text-sm text-gray-600">
                {meta.help}{' '}
                <code className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-xs text-blue-800">
                  {meta.example}
                </code>
              </p>
            </div>
          </div>
        </div>
        {body}
      </DialogContent>
    </Dialog>
  );
}

/** Compat: menção de power clássica. */
export function InsertPowerMentionDialog(
  props: Omit<InsertPowerBbcodeDialogProps, 'mode'>
) {
  return <InsertPowerBbcodeDialog {...props} mode="mention" />;
}
